import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Sparkles, Check, X, CheckCheck, XCircle, Pencil, RefreshCw,
  Send, Search, AlertTriangle, User, ChevronDown, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Debounce a function call by `delay` ms per unique key
function useDebounceMap(delay = 400) {
  const timers = useRef({});
  return useCallback((key, fn) => {
    if (timers.current[key]) clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => { fn(); delete timers.current[key]; }, delay);
  }, [delay]);
}

// ─── Reusable inline user picker ───────────────────────────────────────────
function UserPicker({ users, value, onChange, placeholder = 'Assign user…' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = search
    ? users.filter(u =>
        u.username.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase()))
    : users;

  return (
    <div className="relative" ref={ref}>
      <div
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-2.5 h-7 rounded-full border cursor-pointer text-xs transition-colors select-none
          ${value ? 'border-primary/40 bg-primary/5 text-foreground' : 'border-border hover:border-primary/40 text-muted-foreground'}`}
      >
        <User className="w-3 h-3 shrink-0" />
        <span className="truncate max-w-[120px]">{value ? value.username : placeholder}</span>
        <ChevronDown className="w-3 h-3 shrink-0 ml-auto" />
      </div>
      {open && (
        <div className="absolute top-8 left-0 z-50 w-56 bg-background border border-border rounded-xl shadow-xl overflow-hidden">
          <div className="flex items-center gap-1.5 px-2.5 py-2 border-b border-border">
            <Search className="w-3 h-3 text-muted-foreground shrink-0" />
            <input
              autoFocus
              className="flex-1 text-xs bg-transparent outline-none"
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="max-h-44 overflow-y-auto">
            {filtered.length === 0
              ? <p className="text-xs text-muted-foreground p-2.5">No users found</p>
              : filtered.map(u => (
                  <div
                    key={u.id}
                    onClick={() => { onChange(u); setOpen(false); setSearch(''); }}
                    className="flex items-center justify-between px-2.5 py-1.5 hover:bg-muted cursor-pointer"
                  >
                    <span className="text-xs font-medium truncate">{u.username}</span>
                    <span className="text-xs text-muted-foreground ml-2 truncate">{u.email}</span>
                  </div>
                ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────
export default function PromptGenerator({ token }) {
  const [platforms, setPlatforms] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [instruction, setInstruction] = useState('');
  const [generating, setGenerating] = useState(false);

  // Queue state — loaded from DB on mount, synced on changes
  const [queue, setQueue] = useState([]);
  // shape per item: {id, title, content, categories, is_duplicate, accepted, attributedUser:{id,username,email}|null, platform_id, platform_name}
  const [queueMeta, setQueueMeta] = useState({ platform_id: '', platform_name: '', instruction: '' });
  const [queueLoading, setQueueLoading] = useState(true);

  const [allUsers, setAllUsers] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Accept-all modal
  const [acceptAllOpen, setAcceptAllOpen] = useState(false);
  const [acceptAllUser, setAcceptAllUser] = useState(null);

  // Debouncer for per-item DB sync
  const debounce = useDebounceMap(350);

  // ── Load queue from DB on mount ──────────────────────────────────────────
  const loadQueue = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/prompt-queue`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = (r.data.items || []).map(item => ({
        ...item,
        // Hydrate attributedUser object from stored fields
        attributedUser: item.attributed_user_id
          ? { id: item.attributed_user_id, username: item.attributed_username || '', email: '' }
          : null,
        editing: false,
      }));
      setQueue(items);
      setQueueMeta(r.data.meta || {});
      if (items.length > 0) {
        setSelectedPlatform(r.data.meta?.platform_id || '');
        setInstruction(r.data.meta?.instruction || '');
      }
    } catch {
      // ignore
    } finally {
      setQueueLoading(false);
    }
  }, [token]);

  useEffect(() => {
    axios.get(`${API}/admin/platforms`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPlatforms(r.data.filter(p => p.status === 'approved')))
      .catch(() => {});
    axios.get(`${API}/admin/users/all`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAllUsers(r.data.users || []))
      .catch(() => {});
    loadQueue();
  }, [token, loadQueue]);

  // ── Sync a single item state change to DB ───────────────────────────────
  const syncItem = useCallback((itemId, patch) => {
    debounce(itemId, () => {
      axios.patch(`${API}/admin/prompt-queue/${itemId}`, patch, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    });
  }, [token, debounce]);

  // ── Generate ─────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!selectedPlatform) return toast.error('Please select a platform');
    if (!instruction.trim()) return toast.error('Please enter an instruction');
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/admin/generate-prompts`,
        { platform_id: selectedPlatform, instruction: instruction.trim(), batch_size: 25 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Backend has already saved the new batch to the queue — reload
      await loadQueue();
      const dupes = (r.data.prompts || []).filter(p => p.is_duplicate).length;
      toast.success(
        `Generated ${r.data.prompts?.length || 0} prompts · saved to queue${dupes ? ` · ${dupes} possible duplicates auto-rejected` : ''}`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // ── Accept / Reject toggle ───────────────────────────────────────────────
  const toggleAccept = (idx) => {
    setQueue(prev => {
      const updated = prev.map((p, i) => i === idx ? { ...p, accepted: !p.accepted } : p);
      const item = updated[idx];
      syncItem(item.id, { accepted: item.accepted });
      return updated;
    });
  };

  // ── Assign user ──────────────────────────────────────────────────────────
  const assignUser = (idx, user) => {
    setQueue(prev => {
      const updated = prev.map((p, i) =>
        i === idx ? { ...p, attributedUser: user, accepted: true } : p
      );
      const item = updated[idx];
      syncItem(item.id, {
        accepted: true,
        attributed_user_id: user.id,
        attributed_username: user.username,
      });
      return updated;
    });
  };

  // ── Accept All modal ─────────────────────────────────────────────────────
  const confirmAcceptAll = () => {
    if (!acceptAllUser) return toast.error('Please select a user');
    setQueue(prev => {
      const updated = prev.map(p => ({ ...p, accepted: true, attributedUser: acceptAllUser }));
      // Batch sync all items
      updated.forEach(item => {
        syncItem(item.id, {
          accepted: true,
          attributed_user_id: acceptAllUser.id,
          attributed_username: acceptAllUser.username,
        });
      });
      return updated;
    });
    setAcceptAllOpen(false);
    toast.success(`All prompts accepted and attributed to ${acceptAllUser.username}`);
  };

  const rejectAll = () => {
    setQueue(prev => {
      const updated = prev.map(p => ({ ...p, accepted: false }));
      updated.forEach(item => syncItem(item.id, { accepted: false }));
      return updated;
    });
  };

  // ── Edit helpers ─────────────────────────────────────────────────────────
  const saveEdit = (idx, title, content) => {
    setQueue(prev => prev.map((p, i) => i === idx ? { ...p, title, content, editing: false } : p));
  };

  // ── Publish ──────────────────────────────────────────────────────────────
  const handlePublish = async () => {
    const toPublish = queue.filter(p => p.accepted && p.attributedUser);
    if (toPublish.length === 0) return toast.error('No prompts are ready to publish. Accept prompts and assign users.');
    setPublishing(true);
    try {
      const r = await axios.post(
        `${API}/admin/publish-prompts`,
        {
          platform_id: queueMeta.platform_id || queue[0]?.platform_id,
          prompts: toPublish.map(({ title, content, categories, attributedUser }) => ({
            title, content, categories, user_id: attributedUser.id,
          })),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${r.data.published} prompts published!`);
      // Remove published items from local queue (backend already cleaned DB)
      const publishedTitles = new Set(toPublish.map(p => p.title));
      setQueue(prev => prev.filter(p => !publishedTitles.has(p.title)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  // ── Clear Queue ──────────────────────────────────────────────────────────
  const handleClearQueue = async () => {
    if (!window.confirm('Clear all prompts from the queue? This cannot be undone.')) return;
    setClearing(true);
    try {
      await axios.delete(`${API}/admin/prompt-queue`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQueue([]);
      setInstruction('');
      toast.success('Queue cleared');
    } catch {
      toast.error('Failed to clear queue');
    } finally {
      setClearing(false);
    }
  };

  // ── Derived counts ───────────────────────────────────────────────────────
  const acceptedCount = queue.filter(p => p.accepted).length;
  const readyCount = queue.filter(p => p.accepted && p.attributedUser).length;
  const missingAttributionCount = acceptedCount - readyCount;

  const attributionSummary = (() => {
    const byUser = {};
    queue.filter(p => p.accepted && p.attributedUser).forEach(p => {
      const name = p.attributedUser.username;
      byUser[name] = (byUser[name] || 0) + 1;
    });
    return Object.entries(byUser).map(([name, count]) => `${count} × ${name}`).join(', ');
  })();

  const platformName = queueMeta.platform_name || platforms.find(p => p.id === selectedPlatform)?.name || '';

  return (
    <div className="space-y-6">
      {/* Generation Form */}
      <Card className="p-5 border-border/50 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm">Generate Prompts with AI</h3>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Platform</Label>
          <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
            <SelectTrigger className="rounded-full h-9 text-sm">
              <SelectValue placeholder="Select a platform…" />
            </SelectTrigger>
            <SelectContent>
              {platforms.map(p => (
                <SelectItem key={p.id} value={p.id}>
                  {p.favicon_url && <img src={p.favicon_url} alt="" className="inline w-4 h-4 mr-2 rounded-sm" />}
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Instruction{' '}
            <span className="text-muted-foreground">
              (describe what prompts to generate — AI avoids duplicating popular existing prompts)
            </span>
          </Label>
          <Textarea
            rows={3}
            placeholder={`e.g. "Generate prompts for sales professionals — cold outreach, follow-ups, and objection handling."`}
            value={instruction}
            onChange={e => setInstruction(e.target.value)}
            className="rounded-xl text-sm resize-none"
          />
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Button
            onClick={handleGenerate}
            disabled={generating || !selectedPlatform || !instruction.trim()}
            className="rounded-full gap-2 shadow-lg shadow-primary/20"
          >
            {generating
              ? <><RefreshCw className="w-4 h-4 animate-spin" /> Generating 25 prompts…</>
              : <><Sparkles className="w-4 h-4" /> Generate 25 Prompts</>}
          </Button>
          {queue.length > 0 && !generating && (
            <p className="text-xs text-amber-600 font-medium">
              ⚠ Generating will replace the current {queue.length}-item queue
            </p>
          )}
        </div>
      </Card>

      {/* Queue / Review Board */}
      {queueLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <RefreshCw className="w-4 h-4 animate-spin" /> Loading queue…
        </div>
      ) : queue.length > 0 ? (
        <div className="space-y-3">
          {/* Header bar */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-sm flex items-center gap-2">
                Prompt Queue
                <Badge className="bg-primary/10 text-primary border-primary/20 rounded-full text-xs">{platformName}</Badge>
                <Badge variant="outline" className="rounded-full text-xs text-muted-foreground">
                  {queue.length} in queue
                </Badge>
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {acceptedCount} accepted
                {readyCount > 0 && <> · <span className="text-emerald-600 font-medium">{readyCount} ready to publish</span></>}
                {missingAttributionCount > 0 && (
                  <span className="text-amber-600"> · {missingAttributionCount} need a user assigned</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" className="rounded-full gap-1.5 h-8 text-xs"
                onClick={() => { setAcceptAllUser(null); setAcceptAllOpen(true); }}>
                <CheckCheck className="w-3.5 h-3.5 text-emerald-500" /> Accept All…
              </Button>
              <Button size="sm" variant="outline" className="rounded-full gap-1.5 h-8 text-xs" onClick={rejectAll}>
                <XCircle className="w-3.5 h-3.5 text-red-500" /> Reject All
              </Button>
              <Button
                size="sm"
                className="rounded-full gap-1.5 h-8 text-xs shadow-lg shadow-primary/20"
                onClick={handlePublish}
                disabled={publishing || readyCount === 0}
                title={missingAttributionCount > 0 ? `${missingAttributionCount} accepted prompts still need a user — they will be skipped` : ''}
              >
                {publishing
                  ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Publishing…</>
                  : <><Send className="w-3.5 h-3.5" /> Publish {readyCount}</>
                }
              </Button>
              <Button
                size="sm" variant="ghost"
                className="rounded-full gap-1.5 h-8 text-xs text-muted-foreground hover:text-destructive"
                onClick={handleClearQueue}
                disabled={clearing}
              >
                <Trash2 className="w-3.5 h-3.5" /> Clear Queue
              </Button>
            </div>
          </div>

          {/* Attribution summary */}
          {attributionSummary && (
            <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
              Will publish as: {attributionSummary}
            </p>
          )}

          {/* Prompt cards */}
          <div className="space-y-2">
            {queue.map((p, idx) => (
              <PromptReviewCard
                key={p.id}
                prompt={p}
                index={idx}
                allUsers={allUsers}
                onToggleAccept={() => toggleAccept(idx)}
                onAssignUser={(user) => assignUser(idx, user)}
                onEdit={() => setQueue(prev => prev.map((x, i) => i === idx ? { ...x, editing: true } : x))}
                onSave={(title, content) => saveEdit(idx, title, content)}
              />
            ))}
          </div>

          {/* Sticky bottom publish */}
          <div className="sticky bottom-4 flex justify-end pt-2">
            <Button
              className="rounded-full gap-2 shadow-xl shadow-primary/30 px-6"
              onClick={handlePublish}
              disabled={publishing || readyCount === 0}
              title={missingAttributionCount > 0 ? `${missingAttributionCount} accepted prompts still need a user assigned — they will be skipped` : ''}
            >
              {publishing
                ? <><RefreshCw className="w-4 h-4 animate-spin" /> Publishing…</>
                : <><Send className="w-4 h-4" /> Publish {readyCount} Prompts</>
              }
            </Button>
          </div>
        </div>
      ) : (
        <Card className="p-10 text-center border-border/40 border-dashed">
          <Sparkles className="w-8 h-8 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No prompts in queue.</p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Select a platform, enter an instruction, and generate to fill the queue.
          </p>
        </Card>
      )}

      {/* Accept All modal */}
      <Dialog open={acceptAllOpen} onOpenChange={setAcceptAllOpen}>
        <DialogContent className="w-full max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base">Accept All — Assign Attribution</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              All {queue.length} prompts will be accepted and attributed to the selected user.
              You can still reject individual ones after.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">Attribute all to</Label>
              <UserPickerFull users={allUsers} value={acceptAllUser} onChange={setAcceptAllUser} />
            </div>
            <div className="flex gap-2 pt-1">
              <Button className="flex-1 rounded-full gap-1.5" onClick={confirmAcceptAll} disabled={!acceptAllUser}>
                <CheckCheck className="w-4 h-4" /> Confirm Accept All
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => setAcceptAllOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Full-width user picker for modal ──────────────────────────────────────
function UserPickerFull({ users, value, onChange }) {
  const [search, setSearch] = useState('');
  const filtered = search
    ? users.filter(u =>
        u.username.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase()))
    : users;
  return (
    <div className="border border-border rounded-xl overflow-hidden w-full">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
        <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <input className="flex-1 min-w-0 text-sm bg-transparent outline-none" placeholder="Search username or email…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      <div className="max-h-52 overflow-y-auto">
        {filtered.map(u => (
          <div key={u.id} onClick={() => onChange(u)}
            className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-sm transition-colors
              ${value?.id === u.id ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}`}>
            <span className="font-medium shrink-0 max-w-[140px] truncate">{u.username}</span>
            <span className="text-xs text-muted-foreground truncate min-w-0 flex-1">{u.email}</span>
            {value?.id === u.id && <Check className="w-3.5 h-3.5 shrink-0" />}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Individual prompt review card ─────────────────────────────────────────
function PromptReviewCard({ prompt, index, allUsers, onToggleAccept, onAssignUser, onEdit, onSave }) {
  const [editTitle, setEditTitle] = useState(prompt.title);
  const [editContent, setEditContent] = useState(prompt.content);

  useEffect(() => {
    setEditTitle(prompt.title);
    setEditContent(prompt.content);
  }, [prompt.title, prompt.content]);

  return (
    <Card className={`p-4 border transition-all ${
      prompt.accepted
        ? prompt.is_duplicate
          ? 'border-amber-400/40 bg-amber-50/30 dark:bg-amber-950/10'
          : 'border-emerald-500/30 bg-emerald-500/5'
        : 'border-border/40 opacity-55'
    }`}>
      <div className="flex items-start gap-3">
        <button
          onClick={onToggleAccept}
          className={`mt-0.5 shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
            prompt.accepted ? 'bg-emerald-500 border-emerald-500' : 'border-border hover:border-primary'
          }`}
        >
          {prompt.accepted && <Check className="w-3 h-3 text-white" />}
        </button>

        <div className="flex-1 min-w-0">
          {prompt.editing ? (
            <div className="space-y-2">
              <Input value={editTitle} onChange={e => setEditTitle(e.target.value)}
                className="rounded-lg text-sm font-medium h-8" />
              <Textarea value={editContent} onChange={e => setEditContent(e.target.value)}
                rows={4} className="rounded-lg text-sm resize-none" />
              <div className="flex gap-2">
                <Button size="sm" className="rounded-full h-7 text-xs gap-1" onClick={() => onSave(editTitle, editContent)}>
                  <Check className="w-3 h-3" /> Save
                </Button>
                <Button size="sm" variant="ghost" className="rounded-full h-7 text-xs" onClick={() => onSave(prompt.title, prompt.content)}>
                  <X className="w-3 h-3" /> Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-sm leading-snug">{prompt.title}</p>
                  {prompt.is_duplicate && (
                    <Badge className="rounded-full text-xs gap-1 bg-amber-100 text-amber-700 border-amber-300 h-5 px-2">
                      <AlertTriangle className="w-2.5 h-2.5" /> Possible duplicate
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={onEdit} className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="Edit">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={onToggleAccept} className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    title={prompt.accepted ? 'Reject' : 'Accept'}>
                    {prompt.accepted ? <X className="w-3.5 h-3.5 text-red-500" /> : <Check className="w-3.5 h-3.5 text-emerald-500" />}
                  </button>
                </div>
              </div>

              <p className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-wrap mb-2">{prompt.content}</p>

              <div className="flex flex-wrap items-center gap-2">
                {prompt.categories?.map(c => (
                  <Badge key={c} variant="outline" className="text-xs rounded-full px-2 py-0 h-5">{c}</Badge>
                ))}
                {prompt.accepted && (
                  <UserPicker
                    users={allUsers}
                    value={prompt.attributedUser}
                    onChange={onAssignUser}
                    placeholder="Assign user…"
                  />
                )}
              </div>
            </>
          )}
        </div>

        <span className="text-xs text-muted-foreground/40 shrink-0 mt-0.5">#{index + 1}</span>
      </div>
    </Card>
  );
}
